import React from "react";
import { Watermark, buildData, BrandFrame } from "./shared";
import { LAYOUTS, COLORS } from "./tokens";

/** iter 287 — Estrae un "tipo immobile pulito" dal titolo grezzo.
 *
 * Il title dell'immobile spesso è una frase generata che contiene
 * tipologia + dati misti (es. "Quadrilocale 180 m² Bergamo",
 * "Trilocale ristrutturato con terrazzo"). I template Cover (specialmente
 * Moderno) prendevano solo la prima parola → risultato pessimo quando la
 * city dell'immobile è sporca tipo "Brera · Milano" → output "QUADRILOCALE
 * BRERA" pur essendo l'immobile a Bergamo.
 *
 * Qui isoliamo SOLO il lemma tipologico (Trilocale, Quadrilocale, Attico,
 * Villa, Loft, Monolocale, Studio, Bilocale, Pentalocale ecc.) con
 * fallback a "Immobile". Pulizia case-insensitive.
 */
function cleanPropertyType(title, propertyType) {
  const LEMMI = [
    "monolocale", "bilocale", "trilocale", "quadrilocale", "pentalocale",
    "esalocale", "attico", "villa", "villetta", "loft", "studio",
    "appartamento", "ufficio", "negozio", "rustico", "casale", "duplex",
    "palazzo", "palazzina",
  ];
  const haystack = `${title || ""} ${propertyType || ""}`.toLowerCase();
  for (const l of LEMMI) {
    if (haystack.includes(l)) return l[0].toUpperCase() + l.slice(1);
  }
  // Fallback: prima parola del title se ragionevole
  const first = (title || "").trim().split(/\s+/)[0] || "";
  if (first && /^[A-ZÀ-Ÿa-zà-ÿ]{3,}$/.test(first)) return first[0].toUpperCase() + first.slice(1).toLowerCase();
  return "Immobile";
}

/** iter 287 — Estrae il nome del comune principale dalla city sporca.
 *
 * city può essere "Milano", "Brera, Milano", "Brera · Milano",
 * "Bergamo (BG)". Vogliamo il comune amministrativo, non il quartiere.
 * Strategia: se c'è un separatore tipico (, · - /), prendiamo l'ULTIMA
 * parte (di solito è il comune amministrativo). Altrimenti tutta la city.
 */
function cleanCity(city) {
  if (!city) return "";
  let c = String(city).trim();
  // rimuove eventuali sigle provincia tra parentesi "Bergamo (BG)"
  c = c.replace(/\s*\([^)]+\)\s*$/, "").trim();
  // separatori: virgola, punto centrale, trattino, slash
  const parts = c.split(/\s*[·,\-\/]\s*/).map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1) {
    // l'ultima parte è solitamente il comune (es. "Brera, Milano" → "Milano")
    return parts[parts.length - 1];
  }
  return c;
}

// ─── Template 5: Brochure (4 pagine A4) ──────────────────────────
// iter 312 r14 — Il watermark Pronto compare SOLO sull'ultima pagina
// (contatti) come piccola label "Generata con prontoimmo.it" in basso.
// Le pagine 01/02/03 sono pulite, niente "P prontoimmo.it" che disturbi
// il design delle foto.
export function Brochure({ data, lay, col, watermark }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="static-brochure">
      <BrochurePage label="01 · Cover">
        <BrochureCoverDispatch data={data} lay={lay} col={col} watermark={false} />
      </BrochurePage>
      <BrochurePage label="02 · Descrizione" bg={col.bg === "#1C1C1A" ? "#1C1C1A" : "#FAFAF7"}>
        <BrochureDescriptionDispatch data={data} lay={lay} col={col} />
      </BrochurePage>
      <BrochurePage label="03 · Galleria" bg={col.bg === "#1C1C1A" ? "#1C1C1A" : "#FAFAF7"}>
        <BrochureGalleryDispatch data={data} lay={lay} col={col} />
      </BrochurePage>
      <BrochurePage label="04 · Contatti">
        <BrochureContacts data={data} lay={lay} col={col} showProntoLabel={watermark} />
      </BrochurePage>
    </div>
  );
}

// ─── Brochure cover dispatcher: 4 layout strutturalmente diversi ─
function BrochureCoverDispatch({ data, lay, col, watermark }) {
  if (lay.name === "Elegante")  return <CoverElegante data={data} lay={lay} col={col} watermark={watermark} />;
  if (lay.name === "Luxury")    return <CoverLuxury   data={data} lay={lay} col={col} watermark={watermark} />;
  if (lay.name === "Moderno")   return <CoverModerno  data={data} lay={lay} col={col} watermark={watermark} />;
  if (lay.name === "Minimal")   return <CoverMinimal  data={data} lay={lay} col={col} watermark={watermark} />;
  return null;
}

// ─── Public exports: pagine indipendenti (preview/landing/chip) ──
export function BrochureCover({ layout = "elegante", color = "verde_brera", property, agency, showWatermark = false }) {
  const lay = LAYOUTS[layout] || LAYOUTS.elegante;
  const col = COLORS[color] || COLORS.verde_brera;
  const data = buildData(property, agency);
  return <BrochureCoverDispatch data={data} lay={lay} col={col} watermark={showWatermark} />;
}

export function BrochureDescriptionPage({ layout = "elegante", color = "verde_brera", property, agency }) {
  const lay = LAYOUTS[layout] || LAYOUTS.elegante;
  const col = COLORS[color] || COLORS.verde_brera;
  const data = buildData(property, agency);
  return <BrochureDescriptionDispatch data={data} lay={lay} col={col} />;
}

export function BrochureGalleryPage({ layout = "elegante", color = "verde_brera", property, agency }) {
  const lay = LAYOUTS[layout] || LAYOUTS.elegante;
  const col = COLORS[color] || COLORS.verde_brera;
  const data = buildData(property, agency);
  return <BrochureGalleryDispatch data={data} lay={lay} col={col} />;
}

export function BrochureContactsPage({ layout = "elegante", color = "verde_brera", property, agency }) {
  const lay = LAYOUTS[layout] || LAYOUTS.elegante;
  const col = COLORS[color] || COLORS.verde_brera;
  const data = buildData(property, agency);
  return <BrochureContacts data={data} lay={lay} col={col} />;
}

// ─── Cover variants ──────────────────────────────────────────────
// Cover Elegante: foto piena, sfumatura grande accent in basso, titolo serif italico
// iter v27 r10 — User feedback: «struttura come le Stories per logo + nome
//   + watermark». Quindi sostituiamo l'header inline (logo + agencyName in
//   alto a sinistra) con il `BrandFrame` uniforme che gestisce:
//   • TOP-LEFT  → logo o nome agenzia
//   • BOTTOM-LEFT → nome agente + telefono
//   • BOTTOM-RIGHT → watermark Pronto (se showWatermark)
//   In questo modo la Cover della brochure ha lo stesso pattern delle
//   Stories di Instagram, e il volantino "respira" lo stesso brand.
function CoverElegante({ data, lay, col, watermark }) {
  // iter v28 r439 — User M1351: «30% trasparenza max 40%, sfumata».
  // Stop 60% alzato a `B3` (70% opacità). Resta gradient a 4 stop.
  const overlay = col.isDark
    ? `linear-gradient(to top, ${col.bg} 0%, ${col.bg}F2 28%, ${col.bg}B3 60%, transparent 100%)`
    : `linear-gradient(to top, ${col.accent}F6 0%, ${col.accent}E8 28%, ${col.accent}B3 60%, transparent 100%)`;
  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0" style={{ backgroundImage: `url(${data.photos.hero})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0" style={{ background: overlay }} />
      <div className="relative z-[2] h-full p-4 grid grid-rows-[1fr_auto]" style={{ color: "#FAFAF7" }}>
        {/* Spacer top (il logo ora è in BrandFrame come overlay assoluto) */}
        <div />
        {/* Blocco bottom: titolo + città + linea + prezzo */}
        <div className="mb-12">
          <div
            style={{
              fontFamily: lay.headingFont,
              ...lay.titleStyle,
              fontSize: 22,
              lineHeight: 1.05,
              wordBreak: "break-word",
              maxWidth: "92%",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textShadow: "0 2px 8px rgba(0,0,0,0.4)",
            }}
          >
            {data.title}.
          </div>
          <div className="mt-1.5 text-[12px]" style={{ fontFamily: lay.accentFont, fontStyle: "italic", opacity: 0.92, textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>
            {data.city}
          </div>
          <div className="flex justify-between items-baseline pt-2.5 mt-2 gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.5)" }}>
            {data.showPrice !== false && (
              <span className="flex-shrink-0" style={{ fontFamily: lay.headingFont, fontStyle: "italic", fontSize: 24, textShadow: "0 2px 6px rgba(0,0,0,0.4)" }}>
                {data.price}
              </span>
            )}
            <span className="text-[11px] tracking-[0.18em] uppercase font-semibold opacity-95 truncate ml-auto" style={{ fontFamily: lay.bodyFont, textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>
              {data.surface}mq · {data.rooms} loc.
            </span>
          </div>
        </div>
      </div>
      {/* iter v27 r10 — BrandFrame in stile Stories.
           Logo agenzia top-left · agente+telefono bottom-left · watermark bottom-right. */}
      <BrandFrame data={data} lay={lay} theme="dark" showWatermark={!!watermark} />
    </div>
  );
}

// Cover Luxury: foto centrale incorniciata, ornamenti, Bodoni con tracking.
// Iter 308 r12: l'utente segnalava texts "troppo distanti dalla foto" — il
// flex-1 attorno alla foto creava ~70-100px di spazio vuoto sopra e sotto.
// Ora: header in alto, gruppo (titolo + foto + subtitle) INCOLLATO al
// centro come blocco unico, prezzo ancorato in basso via justify-between.
// Risultato: titolo bacia il bordo superiore della foto, subtitle bacia il
// bordo inferiore, niente air-gap.
function CoverLuxury({ data, lay, col, watermark }) {
  return (
    <div className="absolute inset-0" style={{ background: col.bg, color: col.fg }}>
      <div className="absolute inset-2 pointer-events-none" style={{ border: `1px solid ${col.accent}`, opacity: 0.4 }} />
      <div className="absolute inset-0 p-5 flex flex-col text-center" style={{ justifyContent: "space-between" }}>
        {/* Header agenzia (top) — iter 312 r14: font bumped */}
        <div className="shrink-0">
          {data.agencyLogoUrl && (
            <div className="flex justify-center mb-2">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center overflow-hidden p-1" style={{ background: col.isDark ? "rgba(255,255,255,0.95)" : "rgba(28,28,26,0.05)" }}>
                <img src={data.agencyLogoUrl} alt="" className="max-w-full max-h-full object-contain" crossOrigin="anonymous" />
              </div>
            </div>
          )}
          <div style={{ fontFamily: lay.headingFont, fontStyle: "italic", fontSize: 17, lineHeight: 1.1, wordBreak: "break-word", padding: "0 8px" }}>{data.agencyName}</div>
          {/* iter 312 r14 — rimosso "Real Estate · Esclusiva" duplicato che si
              ripeteva con "Esclusiva" sopra il titolo. Mostriamo solo il web. */}
          {data.agencyWeb && (
            <div className="text-[8.5px] font-bold tracking-[0.32em] uppercase opacity-55 mt-1" style={{ fontFamily: lay.bodyFont }}>{data.agencyWeb}</div>
          )}
        </div>

        {/* Gruppo centrale: titolo + foto + subtitle come blocco unico */}
        <div className="shrink-0">
          <div className="pt-2.5" style={{ borderTop: `1px solid ${col.accent}` }}>
            <div className="text-[8.5px] font-bold tracking-[0.5em] uppercase mb-2" style={{ color: col.accent, fontFamily: lay.bodyFont }}>Esclusiva</div>
            <div style={{ fontFamily: lay.headingFont, ...lay.titleStyle, fontSize: 22, lineHeight: 1.05 }}>{data.title}</div>
          </div>
          <div className="flex items-center justify-center mt-2">
            <div style={{ width: "92%", aspectRatio: "5/4", backgroundImage: `url(${data.photos.hero})`, backgroundSize: "cover", backgroundPosition: "center", border: `4px solid ${col.bg}`, boxShadow: `0 0 0 1px ${col.accent}, 0 10px 18px -10px rgba(0,0,0,0.3)` }} />
          </div>
          {data.subtitle && (
            <div className="text-[8.5px] tracking-[0.16em] mt-2 px-3 opacity-70" style={{ fontFamily: lay.accentFont, fontStyle: "italic" }}>
              « {data.subtitle.length > 60 ? data.subtitle.slice(0, 58) + "…" : data.subtitle} »
            </div>
          )}
        </div>

        {/* iter 312 r14 — prezzo grande (era 16px → 32px), città grande,
            tutto ben centrato. Niente più sensazione "schiacciato in basso". */}
        <div className="shrink-0">
          <div className="text-[10.5px] tracking-[0.28em] uppercase font-semibold" style={{ color: col.softFg }}>{data.city}</div>
          {data.showPrice !== false && (
            <div style={{ fontFamily: lay.headingFont, fontSize: 32, color: col.accent, marginTop: 4, lineHeight: 1, fontStyle: lay.titleStyle.fontStyle === "italic" ? "italic" : "normal" }}>{data.price}</div>
          )}
          <div className="text-[8.5px] tracking-[0.28em] uppercase font-semibold mt-1.5" style={{ color: col.softFg, fontFamily: lay.bodyFont }}>{data.surface} mq · {data.rooms} locali</div>
        </div>
      </div>
      {watermark && <Watermark dark={col.isDark} />}
    </div>
  );
}

// Cover Moderno: split foto/blocco accent, Archivo Black, ref decorativo
// Iter 261: foto era ~50% — troppa banda verde. Ora foto domina al 62%
// e il blocco verde è 38%. "01" criptico → sostituito con il Rif. immobile.
function CoverModerno({ data, lay, col, watermark }) {
  // iter 287 — quando la palette è scura (es. Notte) usare l'accent come
  // sfondo del blocco grande risulta "senape su nero" che cozza. Invertiamo:
  // sfondo = col.bg scuro, testo bianco, accent come bordo/badge.
  const isDark = !!col.isDark;
  const blockBg = isDark ? col.bg : col.accent;
  const blockFg = isDark ? col.fg : "#FFF";
  const dividerCol = isDark ? `${col.accent}99` : "rgba(255,255,255,0.5)";
  const badgeBg = col.accent;
  const badgeFg = isDark ? col.bg : "#FFF";
  return (
    <div className="absolute inset-0 grid" style={{ gridTemplateRows: "1.6fr 1fr", background: "#FFF", color: "#1C1C1A" }}>
      <div className="relative" style={{ backgroundImage: `url(${data.photos.hero})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute top-2 left-2 text-[8px] font-black tracking-[0.2em] uppercase px-1.5 py-1" style={{ background: badgeBg, color: badgeFg, fontFamily: lay.headingFont }}>Nuovo</div>
        <div className="absolute top-2 right-2 text-right flex items-start gap-1.5">
          <div className="flex flex-col text-right">
            <div style={{ fontFamily: lay.headingFont, fontWeight: 900, fontSize: 10, color: "#FFF", textShadow: "0 2px 6px rgba(0,0,0,0.4)", letterSpacing: "-0.01em", textTransform: "uppercase", maxWidth: 110, wordBreak: "break-word", lineHeight: 1.0 }}>{data.agencyName}</div>
            <div className="text-[6px] font-bold tracking-[0.24em] uppercase text-white/85 mt-[2px]" style={{ fontFamily: lay.bodyFont }}>{data.agencyWeb}</div>
          </div>
          {/* iter 312 r11 — logo agenzia in cover */}
          {data.agencyLogoUrl && (
            <div className="w-8 h-8 rounded-sm bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0 p-0.5">
              <img src={data.agencyLogoUrl} alt="" className="max-w-full max-h-full object-contain" crossOrigin="anonymous" />
            </div>
          )}
        </div>
      </div>
      <div className="relative p-3 grid grid-rows-[auto_1fr_auto] gap-1" style={{ background: blockBg, color: blockFg }}>
        <div className="text-[8px] font-black tracking-[0.36em] uppercase opacity-90" style={{ fontFamily: lay.headingFont, color: isDark ? col.accent : blockFg }}>In vendita</div>
        <div style={{ fontFamily: lay.headingFont, fontWeight: 900, fontSize: 22, lineHeight: 0.92, letterSpacing: "-0.025em", textTransform: "uppercase", margin: 0 }}>
          {cleanPropertyType(data.title, data.property_type)} {cleanCity(data.city).toUpperCase()}.
        </div>
        {data.subtitle && (
          <div className="text-[7.5px] leading-snug opacity-90 mt-0.5" style={{ fontFamily: lay.bodyFont, fontWeight: 500 }}>
            {data.subtitle.length > 65 ? data.subtitle.slice(0, 63) + "…" : data.subtitle}
          </div>
        )}
        <div>
          <div className="flex justify-between items-baseline pt-1.5" style={{ borderTop: `1.5px solid ${dividerCol}` }}>
            {data.showPrice !== false ? (
              <span style={{ fontFamily: lay.headingFont, fontWeight: 900, fontSize: 18, letterSpacing: "-0.02em", color: isDark ? col.accent : blockFg }}>{data.priceShort}</span>
            ) : <span />}
            <span className="text-[7px] font-bold tracking-[0.22em] uppercase opacity-90" style={{ fontFamily: lay.headingFont }}>{data.surface}mq · {data.rooms} loc.</span>
          </div>
        </div>
      </div>
      {watermark && <Watermark dark={!isDark} />}
    </div>
  );
}

// Cover Minimal: layout asimmetrico Vignelli — foto a sinistra full-bleed,
// barra verticale color accent e blocco testo a destra. Iter 261: la
// versione precedente (foto centrata 4:3 + tutto sotto) risultava troppo
// vuota → ora bilanciamento sinistra-destra che riempie pagina.
function CoverMinimal({ data, lay, col, watermark }) {
  return (
    <div className="absolute inset-0 grid" style={{ gridTemplateColumns: "1.05fr 0.95fr", background: col.bg, color: col.fg }}>
      {/* Foto sinistra full-bleed top-to-bottom */}
      <div style={{ backgroundImage: `url(${data.photos.hero})`, backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* Colonna destra: barra accent + testo */}
      <div className="relative flex flex-col p-3" style={{ borderLeft: `4px solid ${col.accent}` }}>
        {/* Top: logo + agenzia */}
        <div className="flex items-start gap-2">
          {data.agencyLogoUrl && (
            <div className="w-8 h-8 rounded-sm bg-white border border-[#E5E7E0] flex items-center justify-center overflow-hidden flex-shrink-0 p-0.5">
              <img src={data.agencyLogoUrl} alt="" className="max-w-full max-h-full object-contain" crossOrigin="anonymous" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="break-words" style={{ fontFamily: lay.bodyFont, fontWeight: 700, fontSize: 8, letterSpacing: "0.06em", lineHeight: 1.1 }}>{data.agencyName}</div>
            <div className="text-[6px] font-medium tracking-[0.32em] uppercase opacity-55 mt-[1px] truncate" style={{ fontFamily: lay.bodyFont }}>{data.agencyWeb}</div>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1 min-h-[12px]" />

        {/* Titolo */}
        <div>
          <div className="text-[7.5px] font-light tracking-[0.18em] uppercase mb-1.5 opacity-70" style={{ fontFamily: lay.bodyFont }}>
            {data.property_type || "Trilocale"} · {data.listingType || "Vendita"}
          </div>
          <div style={{ fontFamily: lay.headingFont, fontWeight: 300, fontSize: 19, lineHeight: 1.05, letterSpacing: "-0.025em" }}>
            {cleanCity(data.city)},
            <br />
            <strong style={{ fontWeight: 700 }}>{(data.title.split(" ").find(w => w.length > 5 && !/[0-9]/.test(w)) || "ristrutturato").toLowerCase()}</strong>.
          </div>
          {data.subtitle && (
            <div className="text-[6.5px] leading-snug opacity-65 mt-2" style={{ fontFamily: lay.bodyFont }}>
              {data.subtitle.length > 75 ? data.subtitle.slice(0, 73) + "…" : data.subtitle}
            </div>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1 min-h-[12px]" />

        {/* Bottom: meta + price */}
        <div>
          <div className="text-[7px] font-medium tracking-[0.04em] opacity-65 mb-1.5" style={{ fontFamily: lay.bodyFont }}>
            {data.surface} mq · {data.rooms} locali
          </div>
          {data.showPrice !== false && (
            <div className="flex justify-between items-baseline pt-1.5" style={{ borderTop: `1px solid ${col.fg}` }}>
              <span style={{ fontFamily: lay.bodyFont, fontWeight: 700, fontSize: 13, letterSpacing: "-0.02em" }}>{data.price}</span>
              <span className="text-[6px] font-medium tracking-[0.22em] uppercase opacity-55" style={{ fontFamily: lay.bodyFont }}>In vendita</span>
            </div>
          )}
        </div>
      </div>

      {watermark && <Watermark dark={col.isDark} />}
    </div>
  );
}

// ─── Brochure interior pages — DISPATCHER (iter 312 r13) ─────────
// Ogni layout ha la sua impaginazione P2 e P3, non c'è più una versione
// "one-size-fits-all" con qualche detail switchato. Conseguenza:
//   • Elegante: P2 editoriale (drop cap + body large)  · P3 grid 2×2 generoso
//   • Luxury  : P2 magazine (subtitle pull-quote)      · P3 hero verticale + 4 thumbs
//   • Moderno : P2 asymmetric (titolo huge + chart)    · P3 contact sheet 3×2
//   • Minimal : P2 lineare (1 colonna + sidebar minor) · P3 grid simmetrico 2×2
// Font sizes alzati: nessun testo sotto 7px nel design (era 5.5px = al limite).

function BrochureDescriptionDispatch({ data, lay, col }) {
  if (lay.name === "Luxury")  return <DescriptionLuxury  data={data} lay={lay} col={col} />;
  if (lay.name === "Moderno") return <DescriptionModerno data={data} lay={lay} col={col} />;
  if (lay.name === "Minimal") return <DescriptionMinimal data={data} lay={lay} col={col} />;
  return <DescriptionElegante data={data} lay={lay} col={col} />;
}

function BrochureGalleryDispatch({ data, lay, col }) {
  if (lay.name === "Luxury")  return <GalleryLuxury  data={data} lay={lay} col={col} />;
  if (lay.name === "Moderno") return <GalleryModerno data={data} lay={lay} col={col} />;
  if (lay.name === "Minimal") return <GalleryMinimal data={data} lay={lay} col={col} />;
  return <GalleryElegante data={data} lay={lay} col={col} />;
}

// ─── Helpers ──────────────────────────────────────────────────────
function pageColors(col) {
  const fg = col.bg === "#1C1C1A" ? "#FAFAF7" : "#1C1C1A";
  const softFg = col.bg === "#1C1C1A" ? "rgba(250,250,247,0.72)" : "rgba(28,28,26,0.65)";
  const border = col.bg === "#1C1C1A" ? "rgba(255,255,255,0.14)" : "rgba(28,28,26,0.14)";
  return { fg, softFg, border };
}
function getDescription(data) {
  // iter v27 r12 — niente fallback "di null mq, null locali" se mancano dati.
  // Se l'agente non ha messo descrizione, mostriamo solo titolo+città
  // (essenziale ma pulito, niente "null" imbarazzanti in stampa).
  if (data.description) return data.description;
  if (data.subtitle) return data.subtitle;
  return [data.title, data.city].filter(Boolean).join(" · ");
}
function getAttrs(data) {
  return [
    ["Tipologia", data.property_type || "—"],
    ["Superficie", data.surface ? `${data.surface} mq` : "—"],
    ["Locali", data.rooms || "—"],
    ["Bagni", data.bathrooms || "—"],
    ["Piano", data.floor || "—"],
    ["Classe en.", data.energy_class || "—"],
  ];
}
function PageHeader({ title, num, lay, color, border, transform = "uppercase", italic = false }) {
  return (
    <div className="flex justify-between items-baseline pb-2 border-b" style={{ borderColor: border }}>
      <div style={{ fontFamily: lay.accentFont, fontStyle: italic ? "italic" : "normal", fontWeight: lay.name === "Moderno" ? 900 : 500, fontSize: 13, textTransform: transform, color }}>
        {title}
      </div>
      <div className="text-[8px] tracking-[0.24em] font-bold opacity-55" style={{ fontFamily: lay.bodyFont, color }}>— {num} —</div>
    </div>
  );
}
function PageFooter({ data, num, lay, color, border }) {
  // iter v27 r12 — niente più placeholder "La tua agenzia". Se l'agente non
  // ha messo il nome agenzia, mostriamo solo la città (o il nome agente
  // come ultimo fallback). Se il logo c'è, lo mostriamo PICCOLO nel footer
  // come signature del brand (più professionale del nome scritto).
  const cityClean = cleanCity(data.city);
  const brand = data.agencyName || data.agentName || "";
  return (
    <div className="flex justify-between items-center gap-3 pt-2 border-t" style={{ borderColor: border, fontFamily: lay.bodyFont, color, opacity: 0.7 }}>
      <div className="flex items-center gap-2 min-w-0">
        {data.agencyLogoUrl && (
          <div className="w-4 h-4 flex-shrink-0">
            <img src={data.agencyLogoUrl} alt="" className="w-full h-full object-contain" crossOrigin="anonymous" />
          </div>
        )}
        <span className="truncate text-[9px] tracking-[0.12em] font-semibold uppercase">
          {[brand, cityClean].filter(Boolean).join(" · ")}
        </span>
      </div>
      <span className="flex-shrink-0 text-[9px] tracking-[0.12em] font-semibold uppercase">{num} · 04</span>
    </div>
  );
}
function PhotoBox({ src, lay, fg, label = "Foto in arrivo", style = {} }) {
  // iter 312 r15 — se non c'è foto, placeholder DISCRETO (sfondo sottile,
  // niente testo "FOTO IN ARRIVO" che l'AI critica come unprofessional).
  // Il riquadro si fonde col background ma mantiene la struttura del grid.
  if (!src) {
    return (
      <div
        className="rounded-sm"
        style={{
          background: "linear-gradient(135deg, rgba(124,139,110,0.08) 0%, rgba(124,139,110,0.02) 100%)",
          border: "1px dashed rgba(124,139,110,0.15)",
          ...style,
        }}
      />
    );
  }
  return (
    <div
      className="rounded-sm overflow-hidden"
      style={{ backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center", ...style }}
    />
  );
}

// ─── DESCRIPTION · ELEGANTE (editoriale: drop cap span floated) ──
function DescriptionElegante({ data, lay, col }) {
  const { fg, softFg, border } = pageColors(col);
  const desc = getDescription(data);
  const trimmed = desc.length > 800 ? desc.slice(0, 797) + "…" : desc;
  // iter 312 r17 — html2canvas NON rende ::first-letter pseudo-element.
  // Usiamo span floated reale per drop cap robusto a qualsiasi resolution.
  const firstChar = trimmed.charAt(0);
  const restText = trimmed.slice(1);
  return (
    <div className="h-full p-4 grid grid-rows-[auto_1fr_auto] gap-3" style={{ color: fg }}>
      <PageHeader title="La proprietà" num="02" lay={lay} color={fg} border={border} italic />
      <div className="grid gap-3" style={{ gridTemplateColumns: "1.55fr 1fr" }}>
        <div className="flex flex-col gap-2 min-w-0">
          <div className="text-[8px] font-bold tracking-[0.24em] uppercase" style={{ color: col.accent, fontFamily: lay.bodyFont }}>Descrizione</div>
          <div
            className="text-justify break-words"
            style={{
              fontFamily: lay.bodyFont,
              color: fg,
              fontSize: 9.5,
              lineHeight: 1.55,
            }}
          >
            <span
              style={{
                float: "left",
                fontFamily: lay.headingFont,
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: 34,
                lineHeight: 0.88,
                color: col.accent,
                paddingRight: 5,
                paddingTop: 2,
                marginBottom: -2,
              }}
            >
              {firstChar}
            </span>
            {restText}
          </div>
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          <PhotoBox src={data.photos.p2} lay={lay} fg={fg} style={{ flex: "2 1 0", minHeight: 0 }} />
          <PhotoBox src={data.photos.p3} lay={lay} fg={fg} style={{ flex: "1 1 0", minHeight: 0 }} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-2 border-t" style={{ borderColor: border }}>
        {getAttrs(data).map(([k, v]) => (
          <div key={k} className="px-2 py-1.5 rounded-sm" style={{ background: col.bg === "#1C1C1A" ? "rgba(255,255,255,0.08)" : "rgba(28,28,26,0.05)" }}>
            <div className="text-[8px] font-bold tracking-[0.18em] uppercase" style={{ fontFamily: lay.bodyFont, color: col.accent }}>{k}</div>
            <div className="text-[11px] font-semibold mt-0.5" style={{ fontFamily: lay.headingFont, fontStyle: "italic", color: fg }}>{v}</div>
          </div>
        ))}
      </div>
      <PageFooter data={data} num="02" lay={lay} color={fg} border={border} />
    </div>
  );
}

// ─── DESCRIPTION · LUXURY (magazine: pull-quote subtitle + 2 col) ──
function DescriptionLuxury({ data, lay, col }) {
  const { fg, softFg, border } = pageColors(col);
  const desc = getDescription(data);
  return (
    <div className="h-full p-4 grid grid-rows-[auto_auto_1fr_auto_auto] gap-3" style={{ color: fg }}>
      <PageHeader title="La residenza" num="02" lay={lay} color={fg} border={border} transform="uppercase" italic />
      {data.subtitle && (
        <div className="text-center px-6 py-3" style={{ borderTop: `1px solid ${border}`, borderBottom: `1px solid ${border}` }}>
          <span style={{ fontFamily: lay.headingFont, fontStyle: "italic", fontSize: 14, lineHeight: 1.25, color: col.accent }}>
            "{data.subtitle.length > 130 ? data.subtitle.slice(0, 127) + "…" : data.subtitle}"
          </span>
        </div>
      )}
      <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <PhotoBox src={data.photos.p2} lay={lay} fg={fg} />
        <div className="flex flex-col gap-1" style={{ fontFamily: lay.bodyFont, color: softFg }}>
          <div className="text-[8px] font-bold tracking-[0.28em] uppercase" style={{ color: col.accent }}>Descrizione</div>
          <div style={{ fontSize: 8.5, lineHeight: 1.55, columnCount: 1 }} className="text-justify break-words">
            {desc.length > 800 ? desc.slice(0, 797) + "…" : desc}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-sm" style={{ background: col.bg === "#1C1C1A" ? "rgba(255,255,255,0.08)" : "rgba(28,28,26,0.05)" }}>
        {getAttrs(data).map(([k, v]) => (
          <div key={k} className="text-center min-w-0">
            <div className="text-[8px] font-bold tracking-[0.2em] uppercase truncate" style={{ fontFamily: lay.bodyFont, color: col.accent }}>{k}</div>
            <div className="text-[11px] mt-0.5" style={{ fontFamily: lay.headingFont, fontStyle: "italic" }}>{v}</div>
          </div>
        ))}
      </div>
      <PageFooter data={data} num="02" lay={lay} color={fg} border={border} />
    </div>
  );
}

// ─── DESCRIPTION · MODERNO (asymmetric: titolo huge + 1 photo huge) ──
function DescriptionModerno({ data, lay, col }) {
  const { fg, softFg, border } = pageColors(col);
  const desc = getDescription(data);
  return (
    <div className="h-full p-4 grid grid-rows-[auto_auto_1fr_auto] gap-3" style={{ color: fg }}>
      <PageHeader title="LA CASA" num="02" lay={lay} color={fg} border={border} transform="uppercase" />
      <div style={{ fontFamily: lay.headingFont, fontWeight: 900, fontSize: 30, lineHeight: 0.92, letterSpacing: "-0.03em", textTransform: "uppercase", wordBreak: "break-word" }}>
        {data.title}<span style={{ color: col.accent }}>.</span>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <PhotoBox src={data.photos.p2} lay={lay} fg={fg} />
        <div className="flex flex-col gap-2">
          <div className="text-[7.5px] font-bold tracking-[0.28em] uppercase" style={{ color: col.accent, fontFamily: lay.bodyFont }}>Descrizione</div>
          <div style={{ fontFamily: lay.bodyFont, fontSize: 8.5, lineHeight: 1.5, color: softFg }} className="text-justify break-words">
            {desc.length > 800 ? desc.slice(0, 797) + "…" : desc}
          </div>
          <div className="mt-auto grid grid-cols-2 gap-1.5 pt-2 border-t" style={{ borderColor: border }}>
            {getAttrs(data).map(([k, v]) => (
              <div key={k}>
                <div className="text-[7.5px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: lay.bodyFont, color: col.accent }}>{k}</div>
                <div style={{ fontFamily: lay.headingFont, fontWeight: 900, fontSize: 12, color: fg }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PageFooter data={data} num="02" lay={lay} color={fg} border={border} />
    </div>
  );
}

// ─── DESCRIPTION · MINIMAL (lineare: 1 colonna, sidebar attrs) ──
function DescriptionMinimal({ data, lay, col }) {
  const { fg, softFg, border } = pageColors(col);
  const desc = getDescription(data);
  return (
    <div className="h-full p-5 grid grid-rows-[auto_auto_1fr_auto] gap-4" style={{ color: fg }}>
      <PageHeader title="La proprietà" num="02" lay={lay} color={fg} border={border} transform="uppercase" />
      <div style={{ fontFamily: lay.headingFont, fontSize: 17, lineHeight: 1.1, fontWeight: 500, letterSpacing: "-0.01em", wordBreak: "break-word" }}>
        {data.title}
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: "1.7fr 1fr" }}>
        <div className="flex flex-col gap-2.5">
          <div className="text-[7px] font-bold tracking-[0.3em] uppercase" style={{ color: col.accent, fontFamily: lay.bodyFont }}>Descrizione</div>
          <div style={{ fontFamily: lay.bodyFont, fontSize: 9, lineHeight: 1.6, color: fg }} className="text-justify break-words">
            {desc.length > 800 ? desc.slice(0, 797) + "…" : desc}
          </div>
          {data.photos.p2 && <PhotoBox src={data.photos.p2} lay={lay} fg={fg} style={{ height: 100, marginTop: 6 }} />}
        </div>
        <div className="flex flex-col gap-2 pl-3" style={{ borderLeft: `1px solid ${border}` }}>
          {getAttrs(data).map(([k, v]) => (
            <div key={k} className="pb-2 border-b border-dotted" style={{ borderColor: border }}>
              <div className="text-[8px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: lay.bodyFont, color: col.accent }}>{k}</div>
              <div className="text-[11.5px] font-semibold mt-[2px]" style={{ fontFamily: lay.bodyFont, color: fg }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
      <PageFooter data={data} num="02" lay={lay} color={fg} border={border} />
    </div>
  );
}

// ─── GALLERY · ELEGANTE (grid adaptive che si adatta al numero foto) ──
function GalleryElegante({ data, lay, col }) {
  const { fg, border } = pageColors(col);
  const photos = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4, data.photos.p5].filter(Boolean);
  const extra = Math.max(0, (data.photoTotalCount || 0) - photos.length);
  const hasBottomRow = photos.length >= 4;
  return (
    <div className="h-full p-4 flex flex-col gap-3" style={{ color: fg }}>
      <PageHeader title="Galleria" num="03" lay={lay} color={fg} border={border} italic />
      {/* iter 312 r17 — top grid SEMPRE pieno (hero gridRow span 2, 2 thumb).
          Bottom row 2-up appare solo se ci sono ≥4 foto reali (no più buchi). */}
      <div className="grid gap-2 flex-1" style={{ gridTemplateColumns: "1.6fr 1fr", gridTemplateRows: "1fr 1fr", minHeight: 0 }}>
        <PhotoBox src={photos[0]} lay={lay} fg={fg} style={{ gridRow: "1 / 3", height: "100%" }} />
        <PhotoBox src={photos[1] || photos[0]} lay={lay} fg={fg} style={{ height: "100%" }} />
        <PhotoBox src={photos[2] || photos[1] || photos[0]} lay={lay} fg={fg} style={{ height: "100%" }} />
      </div>
      {hasBottomRow && (
        <div className="grid grid-cols-2 gap-2" style={{ height: 70 }}>
          <PhotoBox src={photos[3]} lay={lay} fg={fg} />
          <PhotoBox src={photos[4] || photos[3]} lay={lay} fg={fg} />
        </div>
      )}
      {extra > 0 && (
        <div className="text-[7px] tracking-[0.22em] uppercase font-semibold opacity-55 text-right" style={{ fontFamily: lay.bodyFont }}>
          + {extra} foto sulla pagina annuncio · {data.agencyWeb || "agenzia.it"}
        </div>
      )}
      <PageFooter data={data} num="03" lay={lay} color={fg} border={border} />
    </div>
  );
}

// ─── GALLERY · LUXURY (hero verticale large + thumbs adattivi) ──
function GalleryLuxury({ data, lay, col }) {
  const { fg, border } = pageColors(col);
  const photos = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4, data.photos.p5].filter(Boolean);
  const extra = Math.max(0, (data.photoTotalCount || 0) - photos.length);
  // iter 312 r17 — se < 5 foto, fallback su righe disponibili senza placeholder
  const thumbs = photos.slice(1, 5);
  while (thumbs.length < 4 && photos.length > 0) thumbs.push(photos[photos.length - 1]);
  return (
    <div className="h-full p-4 grid grid-rows-[auto_1fr_auto] gap-3" style={{ color: fg }}>
      <PageHeader title="La residenza in immagini" num="03" lay={lay} color={fg} border={border} italic />
      <div className="grid gap-2 min-h-0" style={{ gridTemplateColumns: "2fr 1fr" }}>
        <PhotoBox src={photos[0]} lay={lay} fg={fg} style={{ height: "100%" }} />
        <div className="grid grid-rows-4 gap-2 min-h-0">
          {thumbs.map((p, i) => (
            <PhotoBox key={i} src={p} lay={lay} fg={fg} style={{ height: "100%" }} />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: border }}>
        <span className="text-[8px] tracking-[0.24em] uppercase font-bold" style={{ color: col.accent, fontFamily: lay.bodyFont }}>
          Esclusiva
        </span>
        {extra > 0 && (
          <span className="text-[7px] tracking-[0.2em] uppercase font-semibold opacity-55" style={{ fontFamily: lay.bodyFont }}>+ {extra} foto sulla pagina annuncio</span>
        )}
      </div>
      <PageFooter data={data} num="03" lay={lay} color={fg} border={border} />
    </div>
  );
}

// ─── GALLERY · MODERNO (contact sheet adattivo) ──
function GalleryModerno({ data, lay, col }) {
  const { fg, border } = pageColors(col);
  const photos = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4, data.photos.p5, data.photos.p6].filter(Boolean);
  const extra = Math.max(0, (data.photoTotalCount || 0) - photos.length);
  // iter 312 r17 — mostra solo le foto reali, layout adattivo 2-row se ≥4 foto.
  const slots = photos.length >= 6 ? 6 : photos.length >= 4 ? 4 : photos.length;
  const gridCols = slots <= 2 ? 2 : 3;
  return (
    <div className="h-full p-4 grid grid-rows-[auto_1fr_auto] gap-3" style={{ color: fg }}>
      <PageHeader title="LA GALLERIA" num="03" lay={lay} color={fg} border={border} transform="uppercase" />
      <div className={`grid gap-2 grid-cols-${gridCols}`} style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gridAutoRows: "1fr", minHeight: 0 }}>
        {photos.slice(0, slots).map((p, i) => (
          <div key={i} className="relative">
            <PhotoBox src={p} lay={lay} fg={fg} style={{ height: "100%", width: "100%" }} />
            <span className="absolute bottom-1 left-1 text-[6px] font-bold tracking-[0.18em] uppercase px-1.5 py-0.5" style={{ background: col.accent, color: "#FFF", fontFamily: lay.bodyFont }}>
              {String(i + 1).padStart(2, "0")}
            </span>
          </div>
        ))}
      </div>
      {extra > 0 && (
        <div className="text-[8px] tracking-[0.24em] uppercase font-bold text-right" style={{ color: col.accent, fontFamily: lay.bodyFont }}>
          + {extra} ALTRE FOTO ONLINE
        </div>
      )}
      <PageFooter data={data} num="03" lay={lay} color={fg} border={border} />
    </div>
  );
}

// ─── GALLERY · MINIMAL (grid adattivo + subtitle) ──
function GalleryMinimal({ data, lay, col }) {
  const { fg, border } = pageColors(col);
  const photos = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4].filter(Boolean);
  const extra = Math.max(0, (data.photoTotalCount || 0) - photos.length);
  // iter 312 r17 — grid adattiva: 1 colonna se 1-2 foto, 2 colonne se 3+
  const cols = photos.length >= 3 ? 2 : 1;
  const slots = Math.min(photos.length, 4);
  return (
    <div className="h-full p-5 flex flex-col gap-3" style={{ color: fg }}>
      <PageHeader title="Galleria" num="03" lay={lay} color={fg} border={border} transform="uppercase" />
      {data.subtitle && (
        <div className="text-[9px] leading-snug opacity-75 pl-3 border-l-2" style={{ fontFamily: lay.bodyFont, borderColor: col.accent, color: fg }}>
          {data.subtitle.length > 130 ? data.subtitle.slice(0, 127) + "…" : data.subtitle}
        </div>
      )}
      <div className="grid gap-3 flex-1" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridAutoRows: "1fr", minHeight: 0 }}>
        {photos.slice(0, slots).map((p, i) => (
          <PhotoBox key={i} src={p} lay={lay} fg={fg} style={{ height: "100%", width: "100%" }} />
        ))}
      </div>
      {extra > 0 && (
        <div className="text-[7px] tracking-[0.22em] uppercase font-semibold opacity-55 text-right" style={{ fontFamily: lay.bodyFont }}>
          + {extra} foto su {data.agencyWeb || "agenzia.it"}
        </div>
      )}
      <PageFooter data={data} num="03" lay={lay} color={fg} border={border} />
    </div>
  );
}

function BrochureContacts({ data, lay, col, showProntoLabel = true }) {
  // iter v27 r12 — user feedback: «pagina 4 centrami tutto, telefono a
  // sinistra, email a destra». Header agenzia centrato (logo + nome).
  // Agente di riferimento centrato. Telefono ed email in 2 colonne (left/right).
  // Niente più placeholder "La tua agenzia" se manca il nome.
  const agency = data.agencyName || "";
  const agent = data.agentName || "";
  const license = data.agentLicense && data.agentLicense !== "Iscritto al ruolo" ? data.agentLicense : null;
  const phone = data.agentPhone || "";
  const email = data.agentEmail || "";
  return (
    <div className="h-full px-5 py-6 flex flex-col text-white" style={{ background: "#1C1C1A" }}>
      {/* Header agenzia: logo + nome CENTRATI */}
      <div className="flex flex-col items-center gap-3 pb-5 mb-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.18)" }}>
        {data.agencyLogoUrl && (
          <div className="w-16 h-16 rounded bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0 p-1.5">
            <img src={data.agencyLogoUrl} alt="" className="max-w-full max-h-full object-contain" crossOrigin="anonymous" />
          </div>
        )}
        {agency && (
          <div className="text-center min-w-0">
            <div style={{ fontFamily: lay.headingFont, fontStyle: lay.titleStyle.fontStyle === "italic" ? "italic" : "normal", fontWeight: lay.name === "Moderno" ? 900 : 500, fontSize: 19, lineHeight: 1.1, wordBreak: "break-word" }}>{agency}</div>
            {data.agencyWeb && (
              <div className="text-[8px] font-bold tracking-[0.28em] uppercase opacity-55 mt-1" style={{ fontFamily: lay.bodyFont }}>{data.agencyWeb}</div>
            )}
          </div>
        )}
      </div>

      {/* Agente di riferimento — CENTRATO */}
      <div className="flex-1 flex items-center justify-center pb-5">
        <div className="flex flex-col items-center gap-3 text-center">
          {data.agentPhoto && (
            <div className="w-24 h-24 rounded-full bg-white/10 overflow-hidden flex-shrink-0" style={{ boxShadow: `0 0 0 2px ${col.accent}` }}>
              <img src={data.agentPhoto} alt="" className="w-full h-full object-cover" crossOrigin="anonymous" />
            </div>
          )}
          <div className="text-[8px] font-bold tracking-[0.36em] uppercase" style={{ color: col.accent, fontFamily: lay.bodyFont }}>Agente di riferimento</div>
          {agent && (
            <div style={{ fontFamily: lay.headingFont, fontStyle: lay.titleStyle.fontStyle === "italic" ? "italic" : "normal", fontWeight: lay.name === "Moderno" ? 900 : 500, fontSize: 26, lineHeight: 1.1, wordBreak: "break-word" }}>{agent}</div>
          )}
          {license && (
            <div className="text-[8px] tracking-[0.24em] uppercase font-semibold opacity-55" style={{ fontFamily: lay.bodyFont }}>{license}</div>
          )}
        </div>
      </div>

      {/* Telefono a SINISTRA · Email a DESTRA */}
      <div className="grid grid-cols-2 gap-4 pt-5" style={{ borderTop: "1px solid rgba(255,255,255,0.18)" }}>
        {phone ? (
          <div className="min-w-0 text-left">
            <div className="text-[7.5px] font-bold tracking-[0.32em] uppercase opacity-55 mb-1" style={{ fontFamily: lay.bodyFont }}>Telefono</div>
            <div className="break-words" style={{ fontFamily: lay.bodyFont, fontWeight: 600, fontSize: 13, lineHeight: 1.15 }}>{phone}</div>
          </div>
        ) : <div />}
        {email ? (
          <div className="min-w-0 text-right">
            <div className="text-[7.5px] font-bold tracking-[0.32em] uppercase opacity-55 mb-1" style={{ fontFamily: lay.bodyFont }}>Email</div>
            <div className="break-words" style={{ fontFamily: lay.bodyFont, fontWeight: 600, fontSize: 10.5, lineHeight: 1.2 }}>{email}</div>
          </div>
        ) : <div />}
      </div>

      {/* Watermark Pronto SVG (sparisce su Pro) — centrato */}
      {showProntoLabel && (
        <div className="flex items-center justify-center mt-4 gap-1.5 opacity-40">
          <svg viewBox="0 0 48 48" width="11" height="11" fill="none" aria-hidden="true">
            <path d="M 4 22 L 24 6 L 44 22 L 44 42 L 4 42 Z" stroke="#FAFAF7" strokeWidth="2.4" strokeLinejoin="round" fill="none" />
            <path d="M 17 32 L 17 18 L 25 18 Q 31 18 31 23 Q 31 28 25 28 L 17 28" stroke="#FAFAF7" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          <span className="text-[8px] tracking-[0.2em] uppercase font-semibold" style={{ fontFamily: lay.bodyFont }}>prontoimmo.it</span>
        </div>
      )}
    </div>
  );
}

function BrochurePage({ children, label, bg = "#FAFAF7" }) {
  return (
    <div data-testid="brochure-page-wrap">
      <div className="relative rounded-sm overflow-hidden" data-testid="brochure-page" style={{ aspectRatio: "210/297", background: bg, boxShadow: "0 12px 30px -12px rgba(0,0,0,0.5)" }}>
        {/* iter 312 r13 — design canonico A4 = 480×679 (bumped da 371×525)
            per testi più leggibili nella preview e nel PDF esportato. */}
        <ScaledA4Container A4_W={480} A4_H={679}>
          {children}
        </ScaledA4Container>
      </div>
      <div className="text-center mt-2 text-[9px] font-bold tracking-[0.22em] uppercase" style={{ color: "#7C8B6E" }}>{label}</div>
    </div>
  );
}


// ─── BrochureSocial: 4 pagine brochure adattate a carosello Instagram 4:5
// ─────────────────────────────────────────────────────────────────────────
// Le 4 pagine brochure A4 vengono renderizzate ciascuna dentro uno slot
// 4:5 Instagram-native. La pagina A4 (210×297) ha ratio 0.707, il canvas
// 4:5 (1080×1350) ha ratio 0.8 — più "stretto" verticalmente. La pagina
// viene scalata per FIT verticale e centrata orizzontalmente con padding
// del bg-palette: l'effetto è una "carta" che mantiene IDENTICO il design
// della brochure stampabile.
//
// Pattern di anteprima: thumbnail 4 slide in cima + slide attiva grande
// sotto, con CaretLeft/CaretRight per navigare. Le 4 slide a risoluzione
// nativa stanno in un nodo off-screen che html2canvas (staticExport.js)
// può catturare per generare lo ZIP carosello.
export function BrochureSocial({ data, lay, col, watermark }) {
  const [activeIdx, setActiveIdx] = React.useState(0);
  const pages = [
    { idx: 1, kind: "cover",       label: "Copertina"   },
    { idx: 2, kind: "description", label: "Descrizione" },
    { idx: 3, kind: "gallery",     label: "Galleria"    },
    { idx: 4, kind: "contacts",    label: "Contatti"    },
  ];

  const renderPage = (kind) => {
    if (kind === "description") return <BrochureDescriptionDispatch data={data} lay={lay} col={col} />;
    if (kind === "gallery")     return <BrochureGalleryDispatch data={data} lay={lay} col={col} />;
    if (kind === "contacts")    return <BrochureContacts data={data} lay={lay} col={col} />;
    return <BrochureCoverDispatch data={data} lay={lay} col={col} watermark={watermark} />;
  };

  return (
    <div data-testid="static-brochure_social">
      {/* Thumbnails per navigare */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {pages.map((p, i) => (
          <button
            key={p.idx}
            type="button"
            onClick={() => setActiveIdx(i)}
            className={`relative rounded overflow-hidden border-2 transition-all ${
              activeIdx === i ? "border-[#7C8B6E] shadow-md" : "border-transparent opacity-55 hover:opacity-100"
            }`}
            style={{ aspectRatio: "4/5" }}
            data-testid={`brochure-social-thumb-${p.idx}`}
          >
            <SocialSlideFrame data={data} lay={lay} col={col}>
              {renderPage(p.kind)}
            </SocialSlideFrame>
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[8px] font-bold tracking-[0.18em] uppercase text-center py-1">
              {String(p.idx).padStart(2, "0")} · {p.label}
            </div>
          </button>
        ))}
      </div>

      {/* Anteprima grande della slide attiva */}
      <div className="relative mx-auto rounded overflow-hidden shadow-lg" style={{ maxWidth: 420, aspectRatio: "4/5" }}>
        <SocialSlideFrame data={data} lay={lay} col={col}>
          {renderPage(pages[activeIdx].kind)}
        </SocialSlideFrame>
      </div>

      {/* Render farm: 4 slide a risoluzione nativa 1080×1350 per l'export
          html2canvas. Posizionate `position: fixed` con opacity quasi-zero
          + pointer-events none → il browser le RENDE EFFETTIVAMENTE (incluse
          le <img> con loading="lazy"), ma l'utente non le vede. Spostarle
          a `left: -99999` impedisce al browser di caricare le immagini, che
          è esattamente il bug che generava PNG bianche. */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 1080,
          height: 1350 * 4,
          opacity: 0.001,
          pointerEvents: "none",
          zIndex: -1,
          overflow: "hidden",
        }}
        data-testid="brochure-social-export-farm"
      >
        {pages.map((p, i) => (
          <div
            key={p.idx}
            data-testid={`static-brochure_social-slide-${p.idx}`}
            style={{
              position: "absolute",
              top: i * 1350,
              left: 0,
              width: 1080,
              height: 1350,
              background: col.bg,
              overflow: "hidden",
            }}
          >
            <SocialSlideFrame data={data} lay={lay} col={col} native>
              {renderPage(p.kind)}
            </SocialSlideFrame>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Frame 4:5 che ospita una pagina A4. Strategy: fit-height totale →
 *  la pagina A4 occupa TUTTA l'altezza disponibile, i margini palette
 *  stanno solo ai lati (la A4 è più stretta del 4:5).
 *
 *  iter 312 r5 — TUTTE le varianti (thumbnail, preview, native) usano la
 *  STESSA design-size A4 (371 × 525). Il contenitore di destinazione
 *  applica `transform: scale()` per upscalare/downscalare proporzionalmente.
 *  Conseguenza: testi, padding, watermark — tutto resta visivamente
 *  identico tra thumb piccolo, preview grande e PNG esportato a 1080×1350.
 *  Prima: native renderizzava i font a "30px su 1080px" (= 2.8% width =
 *  TROPPO PICCOLI), thumbnail renderizzava a "30px su 80px" (= 37.5% width
 *  = TROPPO GRANDI). */
function SocialSlideFrame({ children, col, native = false }) {
  // iter 312 r13 — bumped da 371×525 a 480×679 (+30%): permette font di
  // 7-10px nel design senza essere claustrofobici e a export 3508 wide
  // diventano ~52-73px = leggibili a stampa.
  const A4_W = 480;
  const A4_H = 679;
  if (native) {
    // Native: slide 1080×1350, A4 fit-height → scaledH = 1350, scaledW = 1350 × 210/297 ≈ 955
    const slideW = 1080;
    const slideH = 1350;
    const scale = slideH / A4_H; // 2.571
    const scaledW = A4_W * scale; // ~954
    const offX = (slideW - scaledW) / 2;
    return (
      <div style={{ position: "absolute", inset: 0, background: col.bg }}>
        <div style={{ position: "absolute", left: offX, top: 0, width: scaledW, height: slideH, background: "#FFF", overflow: "hidden" }}>
          <div style={{ width: A4_W, height: A4_H, transform: `scale(${scale})`, transformOrigin: "top left", position: "absolute", inset: 0 }}>
            {children}
          </div>
        </div>
      </div>
    );
  }
  // Preview/thumbnail responsive: scaliamo il design A4_W×A4_H tramite
  // ResizeObserver-free CSS — usiamo `height: 100%; aspectRatio: 210/297`
  // e il design fisso interno con scale calcolato via CSS contained.
  // Soluzione pulita: design wrapper a A4_W×A4_H, scaliamo con CSS percent.
  // Usiamo aspect-ratio per ricavare la altezza dal genitore, poi width
  // proporzionale, e dentro `transform: scale(h_attuale/525)`.
  return (
    <div style={{ position: "absolute", inset: 0, background: col.bg, display: "flex", justifyContent: "center", alignItems: "stretch" }}>
      <ScaledA4Container A4_W={A4_W} A4_H={A4_H}>
        {children}
      </ScaledA4Container>
    </div>
  );
}

/** Wrapper che misura l'altezza disponibile e applica transform: scale
 *  per mantenere il design A4 (371×525) sempre proporzionato. */
function ScaledA4Container({ A4_W, A4_H, children }) {
  const ref = React.useRef(null);
  const [scale, setScale] = React.useState(1);
  React.useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () => {
      const h = el.clientHeight || 1;
      setScale(h / A4_H);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [A4_H]);
  const scaledW = A4_W * scale;
  return (
    <div
      ref={ref}
      style={{
        height: "100%",
        width: scaledW,
        background: "#FFF",
        boxShadow: "0 8px 18px -8px rgba(0,0,0,0.18)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div style={{ width: A4_W, height: A4_H, transform: `scale(${scale})`, transformOrigin: "top left", position: "absolute", inset: 0 }}>
        {children}
      </div>
    </div>
  );
}
